import { Container, createTheme, ThemeProvider } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import * as React from 'react';
import { ComponentProps } from 'react';
import { div } from '../common';
import { Flex } from '../flex';

export const theme = createTheme({
    typography : {
        fontFamily : 'Montserrat',
        fontSize: 10
    },

    palette : {
        primary : {
            main : '#01C38D',
        },

        secondary : {
            main : "#FFF"
        }
    },

    props: {
        // Name of the component ⚛️
        MuiButtonBase: {
          // The properties to apply
          //disableRipple: true, // No more ripple, on the whole application 💣!
        },
    },

    overrides : {
        MuiTab : {
            root : {
                '&:focus' : {
                    outline: 'none',
                },

                fontSize: 12,
                minHeight: 0,

                '&$selected' : {
                    outline: 'none'
                }
            }
        },

        MuiTabs : {
            root : {
                minHeight: 0,
            }
        },

        MuiPaper : {
            elevation4 : {
                boxShadow : '0px 2px 4px 0px rgba(0,0,0,0.1)'
            },
            
            elevation1 : {
                boxShadow: '2px 0px 3px 0px rgba(0,0,0,0.04)'
            }
        },

        MuiButton : {

        },

        MuiIconButton : {
            root : {
                '&:focus' : {
                    outline: 'none',
                },

                marginLeft: 2,
                marginRight: 2,
            },
        },

        MuiAccordionSummary : {
            root : {
                userSelect: 'text',
                alignItems : 'center',
                minHeight: '40px !important',
                '&$expanded' : {
                    margin : 0
                }
            },

            content : {
                margin: '8px 0',
                alignItems: 'center',
                '&$expanded' : {
                    margin : 0
                }
            }
        },
        MuiLink : {
            root : {
                color: '#1084D3 !important',
                fontFamily: 'Montserrat',
                fontSize: 14,
                fontWeight: 500,
            }
        }
    }
});

export interface PageLayoutProps {
    header? : JSX.Element
    subheader? : JSX.Element | number

    menu? : JSX.Element
    children : JSX.Element[] | JSX.Element
    footer? : JSX.Element

    classes? : {
        root?: string,
        header? : string,
        subheader? : string,
        menu?: string,
        footer? : string,
    },

    menuWidth? : number
    maxWidth? : ComponentProps<typeof Container>['maxWidth']
}

/**
 * Top level page layout. All props are optional.
 */
export const PageLayout = ({ menu, maxWidth, menuWidth = 225, header, footer, subheader, children, classes : userClasses } : PageLayoutProps ) => {
    const classes = PageLayout.useStyles({ classes : userClasses, menu, menuWidth, subheader });
    return (
        <ThemeProvider theme={theme}>
            <div className={ classes.root }>
                { header &&
                    <div className={ classes.header }>
                        { typeof subheader === 'number' ? <div className={ classes.subheader }/> : undefined }
                        { header }
                    </div>
                }

                { subheader && typeof subheader !== 'number' ?
                    <div className={ classes.subheader }>
                        { subheader }
                    </div>
                : undefined }

                <div className={ classes.viewport }>
                    { menu &&
                        <div className={ classes.menu }>
                            { menu }
                        </div>
                    }

                    <div className={ classes.children }>
                        <Container maxWidth={ maxWidth } >
                            { children }
                        </Container>
                    </div>
                </div>

                { footer &&
                    <div>
                        { footer }
                    </div>
                }
            </div>
        </ThemeProvider>
    )
}

export const PageContainer = ( props : {
    header? : JSX.Element,
    offset? : Number,
    children?: React.ReactNode,
    menu?: React.ReactNode
}) => {
    const classes = usePageContainerStyles( props ),
        { children, header, offset } = props;

    return (
        <ThemeProvider theme={theme}>
            <div className={ classes.root }>
                    { props.menu ?
                        <PageContainerMenu>
                            { props.menu }
                        </PageContainerMenu>
                    : null }

                    <Flex column grow>
                        { header ?
                            <div className={ classes.header }>
                                { offset ? <div className={ classes.subheader }/> : undefined }
                                { header }
                            </div>
                        : null }

                        { children }
                    </Flex>
            </div>
        </ThemeProvider>
    )
}

const PageContainerMenu = div({
    backgroundColor : '#052C4C',
    width: 200,
//    position: 'absolute',
//    top: 0,
//    left: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
})

export const Page = ( props : {
    left? : React.ReactNode
    top? : React.ReactNode
    bottom? : React.ReactNode
    children? : React.ReactNode
    header? : React.ReactNode
    maxWidth? : ComponentProps<typeof Container>['maxWidth']
    menuWidth? : number
    classes? : object
}) => {
    const classes = usePageStyles( props ),
        { left, top, header, children, bottom, maxWidth } = props;

    return <>
        { top ?
            <div className={ classes.subheader }>
                { top }
            </div>
        : undefined }

        <div className={ classes.viewport }>
            { left &&
                <div className={ classes.menu }>
                    { left }
                </div>
            }

            <div className={ classes.children }>
                <Container maxWidth={ maxWidth } >
                    { header ?
                        <div className={ classes.header }>
                            { header }
                        </div>
                    : null }
                    { children }
                </Container>
            </div>
        </div>

        { bottom &&
            <div>
                { bottom }
            </div>
        }        
    </>
}

const usePageContainerStyles = makeStyles({
    root : {
        backgroundColor: '#EFF2F4',
        display: "flex",
        flexDirection: "row",
        margin: 0,
        height: "100%",
        width: '100%',
        position : 'relative',
        
        '& .MuiContainer-maxWidthXl' : {
            maxWidth: 1400
        },

        '& .MuiContainer-root' : {
            height: '100%'
        }
    },

    header : {
        color: 'white',
        padding: 10,
        zIndex: 1,
        position: 'relative',
        backgroundColor: '#052C4C',
    },

    subheader : {
        position: 'absolute',
        bottom : 0,
        marginLeft: -10,
        zIndex : -1,
        backgroundColor: '#EFF2F4',
        height : ({ offset } : any ) => -offset,
        width: '100%'
    }
})

const usePageStyles = makeStyles({
    subheader : {
        zIndex: 1
    },

    menu : ({ menuWidth } : any) => ({
        width: menuWidth,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
    }),

    header : {
        width: '100%',
        left: ( props : any ) => props.menu ? props.menuWidth : 0,
    },

    viewport : {
        flex: '1 1 auto',
        position: 'relative',
        flexFlow: 'row',
        display: 'flex',
        minHeight: 200,
    },

    children : {
        flex: '1 1 auto',
        padding: 8,
        bottom: 0,
        top: 0,
        left: ( props : any ) => props.left ? props.menuWidth : 0,
        right: 0,
        position: 'absolute',
        overflowY: 'auto',
    }
});

PageLayout.useStyles = makeStyles({
    root : {
        backgroundColor: '#EFF2F4',
        display: "flex",
        flexFlow: "column",
        height: "100%",
        position : 'relative',
        
        '& .MuiContainer-maxWidthXl' : {
            maxWidth: 1400
        },

        '& .MuiContainer-root' : {
            height: '100%'
        }
    },

    header : {
        color: 'white',
        padding: 10,
        zIndex: 1,
        position: 'relative',
        backgroundColor: '#052C4C',
    },

    subheader : ({ subheader } : any ) => typeof subheader === 'number' ?
        {
            position: 'absolute',
            bottom : 0,
            marginLeft: -10,
            zIndex : -1,
            backgroundColor: '#EFF2F4',
            height : -subheader,
            width: '100%'
        } : {},

    menu : ({ menuWidth } : any) => ({
        width: menuWidth,
        padding: 8
    }),

    viewport : {
        flex: '1 1 auto',
        position: 'relative',
        flexFlow: 'row',
        display: 'flex',
        minHeight: 200,
    },

    children : ( props : any ) => ({
        flex: '1 1 auto',
        padding: 8,
        bottom: 0,
        top: 0,
        left: props.menu ? props.menuWidth : 0,
        right: 0,
        position: 'absolute',
        overflowY: 'auto',
    })
});